import React, { useEffect, useState } from "react";
import { Loader2, Video, Download, Trash2, Film, Sparkles } from "lucide-react";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { api, API_BASE } from "@/lib/api";
import { toast } from "sonner";
import { appConfirm } from "@/components/AppDialogHost";

/**
 * NewsVideoStudio — turns a news story (with its anchor-mix audio)
 * into a branded vertical/square/landscape MP4 with waveform overlay,
 * headline, captions, and station tag. Server renders via ffmpeg.
 *
 * Props:
 *   storyId   — news_stories.story_id
 *   voice     — current anchor voice (passed through for auto-mix fallback)
 *   audioDur  — radio_15 | radio_30 | radio_60 | radio_120
 *   hasScript — whether the chosen script exists in story.generated
 */
export default function NewsVideoStudio({ storyId, voice, audioDur, hasScript }) {
  const [meta, setMeta] = useState({ formats: [], themes: [] });
  const [fmt, setFmt] = useState("vertical");
  const [theme, setTheme] = useState("amber_dark");
  const [captions, setCaptions] = useState(true);
  const [videos, setVideos] = useState([]);
  const [busy, setBusy] = useState(false);
  const [loadingList, setLoadingList] = useState(true);

  useEffect(() => {
    api.get("/news/video-formats").then((r) => setMeta(r.data || { formats: [], themes: [] })).catch(() => {});
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storyId]);

  const refresh = async () => {
    setLoadingList(true);
    try {
      const { data } = await api.get(`/news/stories/${storyId}/videos`);
      setVideos(data || []);
    } catch { /* noop */ }
    finally { setLoadingList(false); }
  };

  const render = async () => {
    if (!hasScript) { toast.error("Generate the script first (Step 1)"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/news/stories/${storyId}/video`, {
        fmt, theme, include_captions: captions, duration_key: audioDur, voice,
      });
      toast.success(`Video ready · ${data.width}x${data.height}`);
      setVideos((v) => [data, ...v]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Video render failed");
    } finally {
      setBusy(false);
    }
  };

  const removeVideo = async (id) => {
    if (!await appConfirm("Delete this video?")) return;
    try {
      await api.delete(`/news/videos/${id}`);
      setVideos((v) => v.filter((x) => x.video_id !== id));
    } catch { toast.error("Delete failed"); }
  };

  const dl = async (v) => {
    try {
      const res = await api.get(`/news/videos/${v.video_id}/file`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `news-${v.story_id}-${v.fmt}.mp4`;
      document.body.appendChild(a); a.click();
      setTimeout(() => { URL.revokeObjectURL(url); a.remove(); }, 500);
    } catch { toast.error("Download failed"); }
  };

  return (
    <section className="rounded-xl border border-line bg-bg-surface p-6" data-testid="news-video-panel">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-brand-amber/80">// STEP 2.5</div>
          <div className="font-display text-xl font-bold flex items-center gap-2">
            <Film className="h-5 w-5 text-brand-amber" /> AI Video News Studio
          </div>
          <p className="text-xs text-ink-muted">
            Convert this story into a branded vertical/square/landscape video — perfect for Reels, Shorts &amp; TikTok.
          </p>
        </div>
        <button
          onClick={render}
          disabled={busy || !hasScript}
          data-testid="news-video-render"
          className="inline-flex items-center gap-2 rounded-md bg-brand-amber px-5 py-2.5 text-sm font-semibold text-black amber-glow disabled:opacity-50"
          title={hasScript ? "" : "Generate the script first"}
        >
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Video className="h-4 w-4" />}
          {busy ? "Rendering…" : "Render video"}
        </button>
      </div>

      <div className="grid sm:grid-cols-3 gap-3 mb-4">
        <div>
          <Label className="text-xs uppercase tracking-wider text-ink-secondary">Format</Label>
          <Select value={fmt} onValueChange={setFmt}>
            <SelectTrigger className="mt-1 bg-bg border-line" data-testid="news-video-fmt"><SelectValue /></SelectTrigger>
            <SelectContent>
              {(meta.formats || []).map((f) => (
                <SelectItem key={f.id} value={f.id} className="text-xs">
                  <div className="flex flex-col">
                    <span>{f.icon} {f.label}</span>
                    <span className="text-[10px] text-ink-muted">{f.use}</span>
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs uppercase tracking-wider text-ink-secondary">Theme</Label>
          <Select value={theme} onValueChange={setTheme}>
            <SelectTrigger className="mt-1 bg-bg border-line" data-testid="news-video-theme"><SelectValue /></SelectTrigger>
            <SelectContent>
              {(meta.themes || []).map((t) => (
                <SelectItem key={t.id} value={t.id} className="text-xs">{t.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-end justify-between gap-3 rounded-lg border border-line bg-bg-elevated px-3 py-2">
          <div>
            <div className="text-xs">Include captions</div>
            <div className="text-[10px] text-ink-muted">Burns radio script as subtitles</div>
          </div>
          <Switch checked={captions} onCheckedChange={setCaptions} data-testid="news-video-captions" />
        </div>
      </div>

      {/* Renders list */}
      {loadingList ? (
        <div className="text-center py-6 text-ink-muted text-xs">Loading…</div>
      ) : videos.length === 0 ? (
        <div className="border border-dashed border-line rounded-md p-6 text-center text-xs text-ink-muted">
          <Sparkles className="h-4 w-4 text-brand-amber mx-auto mb-2" />
          No videos yet. Pick a format and hit <span className="text-brand-amber font-semibold">Render video</span>.
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="news-video-list">
          {videos.map((v) => (
            <div key={v.video_id} className="rounded-lg border border-line bg-bg-elevated p-3 space-y-2" data-testid={`news-video-card-${v.video_id}`}>
              <div className="flex items-center justify-between gap-2">
                <span className="font-mono text-[10px] uppercase tracking-wider text-brand-amber">{v.fmt} · {v.theme}</span>
                <span className="text-[10px] text-ink-muted">{v.width}×{v.height}</span>
              </div>
              <video
                controls
                preload="metadata"
                src={`${API_BASE}/news/videos/${v.video_id}/file`}
                className="w-full rounded-md bg-black aspect-[9/16] object-contain"
                data-testid={`news-video-player-${v.video_id}`}
              />
              <div className="flex items-center justify-between gap-1.5">
                <span className="text-[10px] text-ink-muted truncate">
                  {(v.size_bytes / 1024 / 1024).toFixed(2)} MB
                </span>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => dl(v)}
                    className="inline-flex items-center gap-1 rounded-md border border-line bg-bg px-2 py-1 text-[10px] hover:text-cyan-300"
                    data-testid={`news-video-download-${v.video_id}`}
                  ><Download className="h-3 w-3" /> Download</button>
                  <button
                    onClick={() => removeVideo(v.video_id)}
                    className="inline-flex items-center rounded-md border border-line bg-bg px-2 py-1 text-[10px] text-rose-300 hover:bg-rose-500/10"
                    data-testid={`news-video-delete-${v.video_id}`}
                  ><Trash2 className="h-3 w-3" /></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
